/**
 * Floating Fusion Graph Legend — Workbench-style popup (opened from toolbar Legend).
 * Anchors just below the Legend toggle on open; drag from the header and resize
 * from all eight edges/corners (same interaction model as GraphLegend / Demo Guide).
 * Surfaces live here as a colour key (click to show/hide), not as toolbar chips.
 */
import React, { useMemo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { GripHorizontal, RotateCcw, Star, X } from 'lucide-react';
import { useFusionStore, ALL_MODULES } from '../../stores/useFusionStore';
import type { ElementDefinition } from 'cytoscape';
import { MODULE_COLOR, MODULE_LABEL, presentEntityClasses, type FusionModule, type FusionShape } from '../../types/fusion';
import { diagramPalette } from '../../constants/diagramPalette';
import { WALLET_ROLE_LABEL } from '../../constants/walletRole';

const WALLET_ROLE_SWATCH: { role: string; swatch: keyof typeof diagramPalette }[] = [
  { role: 'exchange_deposit', swatch: 'cex_deposit_verified' },
  { role: 'exchange_hot', swatch: 'cex' },
  { role: 'card_offramp', swatch: 'card' },
  { role: 'customer_wallet', swatch: 'identifier_upi' },
];

const CASE_COLOURS = ['victim', 'cex', 'cex_deposit_verified', 'mule', 'court', 'card', 'identifier_email'] as const;

const HOW_TO_READ: { label: string; body: string; visual?: 'solid' | 'dashed' | 'star' | 'cluster' | 'gap' }[] = [
  {
    label: 'Colour',
    body: 'Which source the node came from (bank, crypto, social, and so on).',
  },
  {
    label: 'Shape',
    body: 'What kind of thing it is: a circle is a wallet or person, a rounded box an exchange, account or company, a diamond a bridge between worlds.',
  },
  {
    label: 'Border',
    body: 'Risk level. Stronger border = higher risk.',
  },
  {
    label: 'Solid line',
    body: 'A hard link you can defend: same ID, bank ledger row, or official notice.',
    visual: 'solid',
  },
  {
    label: 'Dashed line',
    body: 'A possible match (scored guess). Court-safe view dims these.',
    visual: 'dashed',
  },
  {
    label: 'Gold ring',
    body: 'The case subject, joined across more than one source.',
    visual: 'star',
  },
  {
    label: 'Dashed box',
    body: 'A folded group — click to expand the nodes inside.',
    visual: 'cluster',
  },
];

/** Shown only when the canvas holds a coverage-gap edge (CANVAS02), as the classes list is for other kinds. */
const COVERAGE_GAP_ROW: { label: string; body: string; visual: 'gap' } = {
  label: 'Coverage gap',
  body: 'The run did not read the page of this wallet, so the edge is neither confirmed nor withdrawn.',
  visual: 'gap',
};

/** Shown only when the canvas holds an officer-confirmed subject link (LINK02). */
const SUBJECT_LINK_ROW: { label: string; body: string; visual: 'subject_link' } = {
  label: 'Subject link',
  body: 'Officer-confirmed link from a subject to a wallet with an evidence grade (an assertion, not proof).',
  visual: 'subject_link',
};

type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

interface PanelRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const DEFAULT_WIDTH = 320;
const DEFAULT_HEIGHT = 420;
const MIN_WIDTH = 220;
const MIN_HEIGHT = 200;
const ANCHOR_GAP = 6;

const RESIZE_HANDLES: Array<{ edge: ResizeEdge; className: string; cursor: string }> = [
  { edge: 'n', className: 'top-0 left-2 right-2 h-1.5 -translate-y-1/2', cursor: 'ns-resize' },
  { edge: 's', className: 'bottom-0 left-2 right-2 h-1.5 translate-y-1/2', cursor: 'ns-resize' },
  { edge: 'e', className: 'right-0 top-2 bottom-2 w-1.5 translate-x-1/2', cursor: 'ew-resize' },
  { edge: 'w', className: 'left-0 top-2 bottom-2 w-1.5 -translate-x-1/2', cursor: 'ew-resize' },
  { edge: 'ne', className: 'top-0 right-0 h-3 w-3 -translate-y-1/2 translate-x-1/2', cursor: 'nesw-resize' },
  { edge: 'nw', className: 'top-0 left-0 h-3 w-3 -translate-y-1/2 -translate-x-1/2', cursor: 'nwse-resize' },
  { edge: 'se', className: 'bottom-0 right-0 h-3 w-3 translate-y-1/2 translate-x-1/2', cursor: 'nwse-resize' },
  { edge: 'sw', className: 'bottom-0 left-0 h-3 w-3 translate-y-1/2 -translate-x-1/2', cursor: 'nesw-resize' },
];

function parentBounds(el: HTMLElement | null): { width: number; height: number } {
  const parent = el?.offsetParent as HTMLElement | null;
  if (!parent) {
    return {
      width: typeof window !== 'undefined' ? window.innerWidth : 1200,
      height: typeof window !== 'undefined' ? window.innerHeight : 800,
    };
  }
  return { width: parent.clientWidth, height: parent.clientHeight };
}

function clampRect(rect: PanelRect, bounds: { width: number; height: number }): PanelRect {
  const width = Math.max(MIN_WIDTH, Math.min(rect.width, Math.max(MIN_WIDTH, bounds.width - 8)));
  const height = Math.max(MIN_HEIGHT, Math.min(rect.height, Math.max(MIN_HEIGHT, bounds.height - 8)));
  const maxLeft = Math.max(0, bounds.width - width);
  const maxTop = Math.max(0, bounds.height - height);
  return {
    width,
    height,
    left: Math.min(Math.max(0, rect.left), maxLeft),
    top: Math.min(Math.max(0, rect.top), maxTop),
  };
}

/** Place the panel just below the Legend toolbar button, right-aligned to it. */
function anchorBelowLegendToggle(
  panelEl: HTMLElement | null,
  size: { width: number; height: number } = { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT },
): PanelRect {
  const bounds = parentBounds(panelEl);
  const parent = panelEl?.offsetParent as HTMLElement | null;
  const toggle = document.querySelector('[data-tour="fusion-legend-toggle"]') as HTMLElement | null;

  if (toggle && parent) {
    const t = toggle.getBoundingClientRect();
    const p = parent.getBoundingClientRect();
    return clampRect(
      {
        top: t.bottom - p.top + ANCHOR_GAP,
        left: t.right - p.left - size.width,
        width: size.width,
        height: size.height,
      },
      bounds,
    );
  }

  return clampRect(
    {
      top: 12,
      left: bounds.width - size.width - 12,
      width: size.width,
      height: size.height,
    },
    bounds,
  );
}

function VisualCue({ kind }: { kind: NonNullable<(typeof HOW_TO_READ)[number]['visual']> | 'subject_link' }) {
  if (kind === 'solid') {
    return <span className="inline-block w-5 border-t-2 border-foreground/70 shrink-0" aria-hidden />;
  }
  if (kind === 'dashed') {
    return <span className="inline-block w-5 border-t-2 border-dashed border-foreground/50 shrink-0" aria-hidden />;
  }
  if (kind === 'gap') {
    return <span data-testid="fusion-legend-gap-swatch" className="inline-block w-5 border-t-2 border-dashed border-gray-400 shrink-0" aria-hidden />;
  }
  if (kind === 'subject_link') {
    return <span data-testid="fusion-legend-subject-link-swatch" className="inline-block w-5 border-t-2 border-dashed border-[#0891b2] shrink-0" aria-hidden />;
  }
  if (kind === 'star') {
    return <Star className="w-3 h-3 shrink-0 text-primary" aria-hidden />;
  }
  return (
    <span
      className="inline-block w-3.5 h-3.5 shrink-0 rounded-sm border border-dashed border-foreground/50"
      aria-hidden
    />
  );
}

/** A small outline glyph of one of the three canvas shapes, stroked in the class colour. */
function ShapeGlyph({ shape, color }: { shape: FusionShape; color: string }) {
  const common = { fill: 'none', stroke: color, strokeWidth: 2 } as const;
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden>
      {shape === 'ellipse' && <circle cx="7" cy="7" r="5" {...common} />}
      {shape === 'round-rectangle' && <rect x="1.5" y="3" width="11" height="8" rx="2.5" {...common} />}
      {shape === 'diamond' && <path d="M7 1.5 12.5 7 7 12.5 1.5 7Z" {...common} />}
    </svg>
  );
}

export function FusionGraphLegend({ elements }: { elements?: ElementDefinition[] } = {}) {
  const moduleFilters = useFusionStore((s) => s.moduleFilters);
  const toggleModule = useFusionStore((s) => s.toggleModule);
  const setShowLegend = useFusionStore((s) => s.setShowLegend);
  const classes = useMemo(() => presentEntityClasses(elements || []), [elements]);
  const hasGapEdge = useMemo(
    () => (elements || []).some((el) => (el.data as Record<string, unknown> | undefined)?.coverage_gap === true),
    [elements],
  );
  const hasSubjectLink = useMemo(
    () => (elements || []).some((el) => (el.data as Record<string, unknown> | undefined)?.edge_type === 'subject_link'),
    [elements],
  );
  const howToRead = [
    ...HOW_TO_READ,
    ...(hasGapEdge ? [COVERAGE_GAP_ROW] : []),
    ...(hasSubjectLink ? [SUBJECT_LINK_ROW] : []),
  ];

  const [rect, setRect] = useState<PanelRect>({
    top: 12,
    left: 12,
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
  });

  const panelRef = useRef<HTMLDivElement>(null);
  const interaction = useRef<{
    mode: 'drag' | 'resize';
    edge?: ResizeEdge;
    startX: number;
    startY: number;
    origin: PanelRect;
  } | null>(null);

  const applyClamped = useCallback((next: PanelRect) => {
    setRect(clampRect(next, parentBounds(panelRef.current)));
  }, []);

  const resetPosition = useCallback(() => {
    applyClamped(anchorBelowLegendToggle(panelRef.current, {
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT,
    }));
  }, [applyClamped]);

  // Dock under the Legend button before first paint (and on reopen).
  useLayoutEffect(() => {
    setRect(anchorBelowLegendToggle(panelRef.current));
  }, []);

  const applyClampedRef = useRef(applyClamped);
  useEffect(() => {
    applyClampedRef.current = applyClamped;
  }, [applyClamped]);

  const onPointerMove = useCallback((e: PointerEvent) => {
    const state = interaction.current;
    if (!state) return;
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    const { origin } = state;
    const clamp = applyClampedRef.current;

    if (state.mode === 'drag') {
      clamp({
        ...origin,
        top: origin.top + dy,
        left: origin.left + dx,
      });
      return;
    }

    const edge = state.edge!;
    let { top, left, width, height } = origin;
    if (edge.includes('e')) width = origin.width + dx;
    if (edge.includes('w')) {
      width = origin.width - dx;
      left = origin.left + dx;
    }
    if (edge.includes('s')) height = origin.height + dy;
    if (edge.includes('n')) {
      height = origin.height - dy;
      top = origin.top + dy;
    }
    clamp({ top, left, width, height });
  }, []);

  const onPointerUpRef = useRef<(e: PointerEvent) => void>(() => undefined);

  const onPointerUp = useCallback((e: PointerEvent) => {
    if (!interaction.current) return;
    interaction.current = null;
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUpRef.current);
    window.removeEventListener('pointercancel', onPointerUpRef.current);
    try {
      (e.target as HTMLElement | null)?.releasePointerCapture?.(e.pointerId);
    } catch {
      // Already released.
    }
  }, [onPointerMove]);

  useEffect(() => {
    onPointerUpRef.current = onPointerUp;
  }, [onPointerUp]);

  const beginInteraction = useCallback((
    e: React.PointerEvent,
    mode: 'drag' | 'resize',
    edge?: ResizeEdge,
  ) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    interaction.current = {
      mode,
      edge,
      startX: e.clientX,
      startY: e.clientY,
      origin: { ...rect },
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = mode === 'drag'
      ? 'grabbing'
      : (RESIZE_HANDLES.find((h) => h.edge === edge)?.cursor ?? 'default');
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
  }, [rect, onPointerMove, onPointerUp]);

  useEffect(() => () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerUp);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [onPointerMove, onPointerUp]);

  useEffect(() => {
    const parent = panelRef.current?.offsetParent as HTMLElement | null;
    if (!parent || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      setRect((prev) => clampRect(prev, parentBounds(panelRef.current)));
    });
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={panelRef}
      className="absolute z-30 flex flex-col overflow-hidden rounded-lg border border-border bg-card shadow-lg"
      role="dialog"
      aria-label="Fusion Graph Legend"
      data-testid="fusion-graph-legend"
      data-tour="fusion-legend"
      style={{
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      }}
    >
      <div
        data-testid="fusion-graph-legend-drag-handle"
        onPointerDown={(e) => beginInteraction(e, 'drag')}
        className="flex items-center justify-between gap-2 px-3 py-2 border-b border-border bg-secondary/40 cursor-grab active:cursor-grabbing select-none shrink-0 touch-none"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <GripHorizontal className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0" aria-hidden />
          <div className="min-w-0">
            <div className="text-xs font-semibold text-foreground leading-tight">Graph Legend</div>
            <div className="text-[10px] text-muted-foreground truncate">Drag to move · resize from edges</div>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={resetPosition}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Reset legend position"
            title="Reset position"
            data-testid="fusion-graph-legend-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => setShowLegend(false)}
            className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted"
            aria-label="Close Fusion legend"
            data-testid="fusion-graph-legend-close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="p-3 space-y-3 flex-1 min-h-0 overflow-y-auto styled-scrollbar">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
            Surfaces
          </div>
          <div className="space-y-1" data-tour="fusion-surface-filters">
            {ALL_MODULES.map((m: FusionModule) => {
              const on = moduleFilters[m];
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleModule(m)}
                  className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left text-xs hover:bg-muted/60 transition-colors"
                  title={on ? `Hide ${MODULE_LABEL[m]} surface` : `Show ${MODULE_LABEL[m]} surface`}
                  aria-pressed={on}
                >
                  <span
                    className="inline-block w-3 h-3 rounded-full shrink-0 border border-black/10"
                    style={{ background: MODULE_COLOR[m], opacity: on ? 1 : 0.35 }}
                  />
                  <span className={`flex-1 font-medium ${on ? 'text-foreground' : 'text-muted-foreground line-through'}`}>
                    {MODULE_LABEL[m]}
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {on ? 'on' : 'off'}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            Click a surface to show or hide it on the canvas.
          </p>
          <div className="mt-3" data-testid="fusion-case-colours">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
              Case diagram colours
            </div>
            <div className="space-y-1">
              {CASE_COLOURS.map((key) => (
                <div key={key} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span
                    className="inline-block w-3 h-3 rounded-sm shrink-0 border"
                    style={{ background: diagramPalette[key].fill, borderColor: diagramPalette[key].stroke }}
                  />
                  <span>{diagramPalette[key].meaning}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-3" data-testid="fusion-wallet-roles">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
              Wallet role
            </div>
            <div className="space-y-1">
              {WALLET_ROLE_SWATCH.map(({ role, swatch }) => (
                <div key={role} className="flex items-center gap-2 text-[10px] text-muted-foreground">
                  <span
                    className="inline-block w-3 h-3 rounded-sm shrink-0 border"
                    style={{ background: diagramPalette[swatch].fill, borderColor: diagramPalette[swatch].stroke }}
                  />
                  <span>{WALLET_ROLE_LABEL[role]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {classes.length > 0 && (
          <div className="border-t border-border pt-2" data-testid="fusion-legend-classes">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
              On this canvas
            </div>
            <ul className="space-y-1">
              {classes.map((c) => (
                <li key={c.key} className="flex items-center gap-2 text-[11px]" data-class={c.key}>
                  <span className="w-4 flex justify-center shrink-0"><ShapeGlyph shape={c.style.shape} color={c.style.color} /></span>
                  <span className="flex-1 text-foreground">{c.style.label}</span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">{c.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-border pt-2 space-y-2">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
            How to read the graph
          </div>
          <ul className="space-y-2">
            {howToRead.map((row) => (
              <li key={row.label} className="flex items-start gap-2 text-[11px] leading-snug">
                <span className="mt-0.5 w-4 flex justify-center shrink-0">
                  {row.visual ? <VisualCue kind={row.visual} /> : null}
                </span>
                <span>
                  <span className="font-semibold text-foreground">{row.label}</span>
                  <span className="text-muted-foreground"> — {row.body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {RESIZE_HANDLES.map(({ edge, className: handleClass, cursor }) => (
        <div
          key={edge}
          data-testid={`fusion-graph-legend-resize-${edge}`}
          aria-hidden
          onPointerDown={(e) => beginInteraction(e, 'resize', edge)}
          className={`absolute z-50 touch-none ${handleClass}`}
          style={{ cursor }}
        />
      ))}
    </div>
  );
}
